import { defineStore } from 'pinia';
import { UPDATE_REGISTRANT_GIFT_TRACKER_STATUS } from '~/types/actions';
import {
  SET_REGISTRANT_GIFT_TRACKER_RESULTS,
  SET_REGISTRANT_GIFT_TRACKER_LOADER,
  SET_STORE_ERROR_STATE,
  SET_GIFT_TRACKER_RESULTS_ERROR,
  SET_GIFT_TRACKER_UPDATE_ERROR,
  SET_GIFT_TRACKER_UPDATE_STATUS_ERROR,
} from '~/types/mutations';
import { transformGiftTrackerResults, transformGiftTrackerUpdateStatus } from '~/utils/registrantGiftTrackerUtil';
import { SoftSignInValidation } from '~/utils/SoftSignInValidation.js';
import { httpUpdateAPIHeaders } from '~/utils/APIUtil';
import { generateLogin365FingerPrint } from '~/utils/PersistentLoginUtil';
import { useEnvPropsStore } from './envProps';

import $http from '@vue-util/http';

const REGISTRANT_GIFT_TRACKER_ENDPOINT = '/regsvc/registry/api/v1/registry/getRegistryInfo';
const REGISTRANT_GIFT_TRACKER_STATUS_UPDATE_ENDPOINT = '/regsvc/registry/api/v1/purchase/updatePurchase';

export const useRegistrantGiftTrackerStore = defineStore('registrantGiftTracker', {
  state: () => ({
    registrantGiftTrackerResults: {},
    registrantGiftLoader: true,
    errorState: false,
    giftTrackerResultsError: false,
    giftTrackerUpdateError: false,
    statusCode: false,
  }),
  actions: {
    [SET_REGISTRANT_GIFT_TRACKER_RESULTS](registrantGiftTrackerResults) {
      this.registrantGiftTrackerResults = registrantGiftTrackerResults;
    },
    [SET_REGISTRANT_GIFT_TRACKER_LOADER](registrantGiftLoader) {
      this.registrantGiftLoader = registrantGiftLoader;
    },
    [SET_STORE_ERROR_STATE](payload) {
      this.errorState = payload;
    },
    [SET_GIFT_TRACKER_RESULTS_ERROR](payload) {
      this.giftTrackerResultsError = payload;
    },
    [SET_GIFT_TRACKER_UPDATE_ERROR](payload) {
      this.giftTrackerUpdateError = payload;
    },
    [SET_GIFT_TRACKER_UPDATE_STATUS_ERROR](payload) {
      this.statusCode = payload;
    },
    async getRegistrantGiftTrackerResults(payload) {
      const envPropsStore = useEnvPropsStore();

      this[SET_STORE_ERROR_STATE](false);
      this[SET_REGISTRANT_GIFT_TRACKER_LOADER](true);
      this[SET_GIFT_TRACKER_RESULTS_ERROR](false);
      const httpSource = $http.CancelToken.source();
      const timer = setTimeout(() => {
        this[SET_REGISTRANT_GIFT_TRACKER_RESULTS]({});
        this[SET_STORE_ERROR_STATE](true);
        this[SET_REGISTRANT_GIFT_TRACKER_LOADER](false);
        httpSource.cancel();
      }, envPropsStore.envProps?.apiTimeout || 0);
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.post(`${REGISTRANT_GIFT_TRACKER_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
          cancelToken: httpSource.token,
        });
        clearTimeout(timer);
        const registryInfo = transformGiftTrackerResults(response.data?.registryInfo);
        this[SET_REGISTRANT_GIFT_TRACKER_RESULTS](registryInfo);
        return response;
      } catch (error) {
        this[SET_GIFT_TRACKER_UPDATE_STATUS_ERROR](true);
        SoftSignInValidation(error);
        clearTimeout(timer);
        this[SET_REGISTRANT_GIFT_TRACKER_RESULTS]({});
        this[SET_GIFT_TRACKER_RESULTS_ERROR](true);
        return error;
      } finally {
        this[SET_REGISTRANT_GIFT_TRACKER_LOADER](false);
      }
    },
    async [UPDATE_REGISTRANT_GIFT_TRACKER_STATUS]({ payload, type }) {
      this[SET_REGISTRANT_GIFT_TRACKER_RESULTS](transformGiftTrackerUpdateStatus(this.registrantGiftTrackerResults, payload, type, 'LOADING'));
      this[SET_GIFT_TRACKER_UPDATE_ERROR](false);
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.put(`${REGISTRANT_GIFT_TRACKER_STATUS_UPDATE_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
        const isError = response?.data?.errors || response?.data?.error;
        this[SET_REGISTRANT_GIFT_TRACKER_RESULTS](
          transformGiftTrackerUpdateStatus(this.registrantGiftTrackerResults, payload, type, isError ? 'ERROR' : 'SUCCESS'),
        );
        return response;
      } catch (error) {
        SoftSignInValidation(error);
        this[SET_REGISTRANT_GIFT_TRACKER_RESULTS](transformGiftTrackerUpdateStatus(this.registrantGiftTrackerResults, payload, type, 'ERROR'));
        this[SET_GIFT_TRACKER_UPDATE_ERROR](true);
        return error;
      }
    },
  },
});
